import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Stack,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  MenuItem,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ReviewsOutlinedIcon from '@mui/icons-material/ReviewsOutlined';
import StarRoundedIcon from '@mui/icons-material/StarRounded';

const STAR_COLOR = '#f59e0b';

const RatingStars = ({ rating }) => (
  <Stack direction="row" spacing={0.25} alignItems="center">
    {[1, 2, 3, 4, 5].map((n) => (
      <StarRoundedIcon key={n} sx={{ fontSize: 16, color: n <= rating ? STAR_COLOR : 'text.disabled' }} />
    ))}
    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{rating}/5</Typography>
  </Stack>
);

const AdminReviews = ({
  reviews,
  reviewsLoading,
  reviewsError,
  reviewsPage,
  reviewsTotalPages,
  showReviewModal,
  editingReview,
  reviewForm,
  reviewFormError,
  reviewFormLoading,
  openReviewModal,
  closeReviewModal,
  handleReviewFormSubmit,
  handleReviewFormChange,
  handleDeleteReview,
  fetchReviews,
}) => {
  const handlePageChange = (_, page) => fetchReviews(page);

  return (
    <>
      <Card sx={{ borderRadius: 3, boxShadow: '0px 16px 45px rgba(15, 23, 42, 0.12)' }}>
        <CardHeader
          title="Reviews"
          subheader="Moderate and manage product and auction reviews"
          titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}
        />
        {reviewsLoading && <LinearProgress color="primary" />}
        <CardContent sx={{ pt: 0 }}>
          {reviewsError && <Alert severity="error" sx={{ mb: 2 }}>{reviewsError}</Alert>}

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 480 }}>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
                  <TableCell>Reviewer</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Comment</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!reviews || reviews.length === 0) && !reviewsLoading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No reviews found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (reviews || []).map((review) => (
                    <TableRow key={review._id} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{review.reviewer?.username || 'N/A'}</Typography>
                      </TableCell>
                      <TableCell>
                        <RatingStars rating={review.rating} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {review.comment || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <IconButton size="small" color="primary" onClick={() => openReviewModal(review)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteReview(review)}>
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {reviewsTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination count={reviewsTotalPages} page={reviewsPage} onChange={handlePageChange} color="primary" shape="rounded" />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Edit / Add Review Dialog */}
      <Dialog
        open={!!showReviewModal}
        onClose={closeReviewModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ReviewsOutlinedIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>{editingReview ? 'Edit Review' : 'Add Review'}</Typography>
          </Stack>
          <IconButton size="small" onClick={closeReviewModal}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2.5} component="form" id="review-form" onSubmit={handleReviewFormSubmit}>
            {reviewFormError && <Alert severity="error">{reviewFormError}</Alert>}
            <TextField
              select
              label="Rating"
              name="rating"
              required
              value={reviewForm?.rating ?? ''}
              onChange={handleReviewFormChange}
              fullWidth
              size="small"
            >
              <MenuItem value="">Select Rating</MenuItem>
              {[1, 2, 3, 4, 5].map((n) => (
                <MenuItem key={n} value={String(n)}>{n} Star{n > 1 ? 's' : ''}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Comment"
              name="comment"
              multiline
              rows={4}
              value={reviewForm?.comment ?? ''}
              onChange={handleReviewFormChange}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={closeReviewModal}>Cancel</Button>
          <Button type="submit" form="review-form" variant="contained" disabled={reviewFormLoading}>
            {reviewFormLoading ? 'Saving…' : editingReview ? 'Update Review' : 'Add Review'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminReviews;
